import React, { useState, useEffect } from 'react';
import { useAppDispatch } from '../hooks/reduxHooks';
import { deleteProject } from '../ProjectSlice_Store';

interface DeleteChatModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
  projectName: string;
  onDeleted: () => void;
}

export default function DeleteChatModal({ isOpen, onClose, projectId, projectName, onDeleted }: DeleteChatModalProps) {
  const dispatch = useAppDispatch();
  const [isDeleting, setIsDeleting] = useState(false);

  // Document-level Escape handler
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isDeleting) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, isDeleting, onClose]);

  if (!isOpen) return null;

  const handleDelete = async () => {
    if (isDeleting) return;
    setIsDeleting(true);
    try {
      await dispatch(deleteProject({ projectId })).unwrap();
      setIsDeleting(false);
      onDeleted();
    } catch {
      // Keep modal open on error so user can retry
      setIsDeleting(false);
    }
  };

  // Prevent closing while delete is in-flight
  const handleClose = () => {
    if (!isDeleting) {
      onClose();
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-50"
        onMouseDown={handleClose}
        aria-hidden="true"
      />

      {/* Dialog */}
      <div
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        role="dialog"
        aria-modal="true"
        aria-labelledby="delete-chat-modal-title"
        onMouseDown={handleClose}
      >
        <div
          className="bg-white rounded-lg shadow-xl max-w-lg w-full"
          onMouseDown={(e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation()}
        >
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 id="delete-chat-modal-title" className="text-lg font-semibold text-gray-900">Delete chat</h2>
        </div>

        {/* Body */}
        <div className="px-6 py-4">
          <p className="text-sm text-gray-700">
            Are you sure you want to delete <span className="font-medium">{projectName}</span>?
          </p>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200">
          <button
            onClick={handleClose}
            disabled={isDeleting}
            className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={handleDelete}
            disabled={isDeleting}
            className="px-4 py-2 text-sm font-medium text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ backgroundColor: '#234E81' }}
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </button>
        </div>
        </div>
      </div>
    </>
  );
}
